import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { RawUrlValue, UrlParamDefinition, UrlStateValues } from './url-state.types';

const MAX_TEXT_LENGTH = 200;
const MAX_LIST_ITEMS = 50;
const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/;

@Injectable({ providedIn: 'root' })
export class UrlStateService {
  private pending: Record<string, RawUrlValue> = {};
  private pushRequested = false;
  private scheduled = false;

  constructor(private router: Router) { }

  get params(): Params {
    return this.router.parseUrl(this.router.url).queryParams;
  }

  read(definitions: UrlParamDefinition[]): UrlStateValues {
    const source = this.params;
    const values: UrlStateValues = {};

    for (const definition of definitions) {
      const raw = source[definition.key];
      if (definition.sensitive) {
        if (raw !== undefined && raw !== null && raw !== '') this.patch({ [definition.key]: null });
        continue;
      }
      const parsed = this.parse(definition, raw);
      if (parsed !== undefined) {
        values[definition.key] = parsed;
      } else if (raw !== undefined && raw !== null && raw !== '') {
        this.patch({ [definition.key]: null });
      }
    }

    return values;
  }

  write(definitions: UrlParamDefinition[], values: UrlStateValues, push = false): void {
    const raw: Record<string, RawUrlValue> = {};

    for (const definition of definitions) {
      raw[definition.key] = definition.sensitive ? null : this.serialize(definition, values[definition.key]);
    }

    this.patch(raw, push);
  }

  patch(values: Record<string, RawUrlValue>, push = false): void {
    Object.assign(this.pending, values);
    this.pushRequested = this.pushRequested || push;

    if (this.scheduled) {
      return;
    }

    this.scheduled = true;
    Promise.resolve().then(() => this.flush());
  }

  private flush(): void {
    const queryParams = this.pending;
    const push = this.pushRequested;
    this.pending = {};
    this.pushRequested = false;
    this.scheduled = false;

    this.router.navigate([], { queryParams, queryParamsHandling: 'merge', replaceUrl: !push });
  }

  private parse(definition: UrlParamDefinition, raw: unknown): any {
    if (typeof raw !== 'string' || raw === '') {
      return undefined;
    }

    switch (definition.type) {
      case 'text': {
        const text = raw.trim().slice(0, MAX_TEXT_LENGTH);
        return text || undefined;
      }
      case 'id':
        return ID_PATTERN.test(raw) ? raw : undefined;
      case 'decimal':
        return /^-?\d{1,12}(?:\.\d{1,2})?$/.test(raw) ? Number(raw) : undefined;
      case 'number': {
        if (!/^\d{1,9}$/.test(raw)) return undefined;
        const value = Number(raw);
        return value < (definition.minValue ?? 0) || value > (definition.maxValue ?? Number.MAX_SAFE_INTEGER) ? undefined : value;
      }
      case 'enum':
        return definition.options?.includes(raw) ? raw : undefined;
      case 'list': {
        const items = [...new Set(raw.split(',').map((item) => item.trim().slice(0, MAX_TEXT_LENGTH)).filter((item) => item && (definition.itemType !== 'id' || ID_PATTERN.test(item)) && (!definition.options || definition.options.includes(item))))].slice(0, MAX_LIST_ITEMS);
        return items.length ? items : undefined;
      }
      case 'datetime':
        return this.parseDate(raw, !!definition.endOfDay);
    }
  }

  private parseDate(raw: string, endOfDay: boolean): Date | undefined {
    const match = DATE_PATTERN.exec(raw);
    if (!match) {
      return undefined;
    }

    const [, year, month, day, hours, minutes] = match;
    const hasTime = hours !== undefined;
    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      hasTime ? Number(hours) : endOfDay ? 23 : 0,
      hasTime ? Number(minutes) : endOfDay ? 59 : 0
    );

    if (hasTime && (Number(hours) > 23 || Number(minutes) > 59)) return undefined;

    const valid = date.getFullYear() === Number(year) && date.getMonth() === Number(month) - 1 && date.getDate() === Number(day);
    return valid ? date : undefined;
  }

  private serialize(definition: UrlParamDefinition, value: unknown): RawUrlValue {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    let text: string;

    switch (definition.type) {
      case 'list':
        text = Array.isArray(value) ? value.join(',') : String(value);
        break;
      case 'datetime':
        text = value instanceof Date ? this.formatDate(value) : '';
        break;
      default:
        text = String(value).trim();
    }

    const parsed = this.parse(definition, text);
    if (parsed === undefined) return null;
    if (definition.type === 'text') text = String(parsed);
    if (definition.type === 'list') text = (parsed as string[]).join(',');

    const defaultValue = definition.defaultValue instanceof Date ? this.formatDate(definition.defaultValue) : String(definition.defaultValue ?? '');
    if (!text || text === defaultValue) {
      return null;
    }

    return text;
  }

  private formatDate(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
}
